import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { queryClient } from "../api";
import { memoirsApiInstance } from "../api/api";
import { toast } from "react-toastify";
import {
  reset,
  selectImpersonating,
  selectUser,
  stopImpersonation,
} from "../store/slices/auth";
import { useDispatch, useSelector } from "../store/store";
import { User } from "../types/models";

type StopImpersonateResponse = {
  user: User;
  token: string;
};

export const ImpersonationBanner = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const impersonating = useSelector(selectImpersonating);
  const impersonatedUser = useSelector(selectUser);
  const [loading, setLoading] = useState(false);

  if (!impersonating) return null;

  // Leaving never depends on the impersonated account (it may be unconfirmed
  // or locked): the request is made with the admin's own token, and if even
  // that fails the session is cleared so nobody stays trapped.
  const handleStop = async () => {
    setLoading(true);
    const impersonatedUserId = impersonatedUser?.id;
    try {
      const res = await memoirsApiInstance.delete<StopImpersonateResponse>(
        "/admin/impersonate",
        {
          headers: {
            Authorization: impersonating.adminToken,
            "X-Admin-Token": impersonating.adminToken,
          },
        },
      );
      dispatch(stopImpersonation({ user: res.user, token: res.token }));
      queryClient.clear();
      // Back to the admin page of the user who was impersonated
      navigate(
        impersonatedUserId
          ? `/admin/users/${impersonatedUserId}`
          : "/admin/users",
      );
    } catch {
      // Admin token expired or revoked: sign out completely
      dispatch(reset());
      queryClient.clear();
      toast.error("Session admin expirée : reconnectez-vous.");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  // Rendered straight under <body>: an open dialog (Headless UI) makes the app
  // root inert, and the "Arrêter" button must stay clickable whatever the
  // impersonated account shows.
  return createPortal(
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-4 bg-amber-500 text-white px-5 py-3 rounded-full shadow-lg text-sm font-medium">
      <span>
        Mode impersonification :{" "}
        <strong>
          {impersonating.adminUser.first_name}{" "}
          {impersonating.adminUser.last_name}
        </strong>{" "}
        →{" "}
        <strong>
          {impersonatedUser?.first_name} {impersonatedUser?.last_name}
        </strong>
      </span>
      <button
        onClick={handleStop}
        disabled={loading}
        className="bg-white text-amber-600 px-3 py-1 rounded-full text-xs font-semibold hover:bg-amber-50 disabled:opacity-50"
      >
        {loading ? "..." : "Arrêter"}
      </button>
    </div>,
    document.body,
  );
};
